import React, { useEffect, useState } from 'react';
  import { useDispatch, useSelector } from 'react-redux';
  import { loginUser } from '../../../app/store/slices/authSlice';
  import { Link, useNavigate } from 'react-router-dom';
  import { FaEye, FaEyeSlash } from 'react-icons/fa';
  import logo from '../../../assets/images/logos/576_Cash Canvas-03.png';
  import { toast } from 'react-toastify';
  import { fetchUserCanvases } from '../../../app/store/slices/canvasSlice';
  import { fetchSubscriptionData } from '../../../app/store/slices/subscriptionSlice';
  import { fetchUserData, selectUser } from '../../../app/store/slices/userSlice';

  export const LoginForm = () => {
    const dispatch = useDispatch(); 
    const navigate = useNavigate();
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const { status } = useSelector((state) => state.auth);
    const user = useSelector(selectUser);

    React.useEffect(() => {
      // Cleanup function to check auth status on mount
      const checkAuth = () => {
        const authData = JSON.parse(localStorage.getItem('authData') || '{}');
        if (authData?.token) {
          navigate('/dashboard', { replace: true });
        }
      };

      checkAuth();
    }, [navigate]);

    useEffect(() => {
      const fetchData = async () => {
        await Promise.all([
          dispatch(fetchSubscriptionData()),
          dispatch(fetchUserCanvases()),
          dispatch(fetchUserData()),
        ]);
      };
      fetchData();
    }, [dispatch]);

    const handleChange = (e) => {
      setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleTogglePassword = () => {
      setShowPassword((v) => !v);
    };

    // Add form validation function
    const isFormValid = () => {
      return formData.email && formData.password;
    };

const handleSubmit = async (e) => {
  e.preventDefault();

  try {
    const result = await dispatch(loginUser(formData)).unwrap();
    const user = result?.data?.user;
    switch (result.status) {
      case 200:
        localStorage.setItem(
          'authData',
          JSON.stringify({
            user,
            accessToken: result.data.accessToken,
          })
        );
        break;

      case 400:
        toast.error('Please enter both email and password');
        return;

      case 401:
        const attempts = result.error?.remainingAttempts;
        if (attempts === 3) {
          toast.error('Incorrect password');
        } else if (attempts === 2) {
          toast.error('Incorrect password. 2 attempts remaining');
        } else if (attempts === 1) {
          toast.error('Incorrect password. 1 attempt remaining');
        } else {
          toast.error('Unauthorized');
        }
        setFormData((prev) => ({ ...prev, password: '' }));
        return;

      case 404:
        toast.error('Email not registered');
        navigate('/signup', { state: { email: formData.email } });
        return;

      case 429:
        toast.error('Account temporarily locked. Please try again in 1 hour.');
        setFormData((prev) => ({ ...prev, password: '' }));
        return;

      case 500:
        toast.error('An error occurred. Please try again later.');
        return;

      default:
        toast.error('An unexpected error occurred. Please try again.');
        return;
    }

    // Block user if suspended or deleted
    if (user.status === 'Suspended') {
      toast.error('Your account is suspended. Please contact support.');
      return;
    } else if (user.status === 'Deleted') {
      toast.error('Your account has been temporarily deleted. Please contact support.');
      return;
    }

    // Admin redirect
    if (user.role === 'admin') {
      navigate('/manage-portal', { replace: true });
      return;
    }

    // Check if user has completed profile
    if (!user.isDetailsEntered) {
      toast.info('Please complete your profile setup first.');
      navigate('/setup-profile');
      return;
    }

    // All checks passed – navigate to dashboard
    if (user.status === 'Active' && user.isDetailsEntered) {
      toast.success('Welcome back!');
      navigate('/dashboard', { replace: true });
    } else {
      throw new Error('Unexpected user status or role');
    }
  } catch (err) {
    if (err.code === 'ERR_NETWORK') {
      toast.error('Network error. Please check your connection.');
    } else if (err.code === 'ECONNABORTED') {
      toast.error('Request timed out. Please try again.');
    
    }else if(err.code === 'EMAIL_NOT_REGISTERED'){
      toast.error('Email not registered. Please sign up.');
      navigate('/signup', { state: { email: formData.email } });
    } else {
      toast.error(err.message || 'Login failed. Please try again.');
    }
  }
};


    return (
      <div className="flex-1 min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="auth-ui-card">
          {/* Logo - top center */}
          <div className="flex justify-center mb-4">
          <img
                src={logo}
                          alt="Cash Canvas Logo"
                          className="h-56 w-auto"
                        />
            </div>
          <h2 className="txt-page-heading">Login</h2>

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Email */}
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              className="input-field"
            />

            {/* Password w/ show/hide */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                required
                className="input-field"
              />
              <button
                type="button"
                onClick={handleTogglePassword}
                className="absolute inset-y-0 right-3 flex items-center text-gray-500"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Forgot Password aligned right */}
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="p-tag-below-heading"
              >
                Forgot Password?
              </Link>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={status === 'loading' || !isFormValid()}
              className={`
                submit-btn
              `}
            >
              {status === 'loading' ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Sign Up Link */}
          <p className="mt-6 text-center font-sans text-sm text-gray-600">
            Don’t have an account?{' '}
            <Link to="/signup" className="text-blue-800 hover:text-btn-primary-hover">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    );
  };